import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import ArticleCard from '@/components/feature/ArticleCard';
import SectionHeading from '@/components/base/SectionHeading';
import { movies } from '@/mocks/movies';
import { articles } from '@/mocks/articles';
import { collections } from '@/mocks/collections';
import HeroCarousel from './components/HeroCarousel';
import CategoryQuickEntry from './components/CategoryQuickEntry';
import PlotList from './components/PlotList';
import CollectionPreview from './components/CollectionPreview';

export default function Home() {
  const featured = [...movies].sort((a, b) => b.heat - a.heat).slice(0, 4);
  const hanziArticles = articles.filter((a) => a.kind === 'hanzi').slice(0, 3);
  const plotArticles = articles.filter((a) => a.kind === 'plot').slice(0, 6);

  return (
    <SiteLayout>
      <HeroCarousel items={featured} />

      <div className="w-full px-4 md:px-8 py-12 md:py-16 space-y-14 md:space-y-20">
        <section>
          <SectionHeading
            icon="ri-apps-2-line"
            title="影视分类快捷入口"
            subtitle="按类型、地区与题材，快速找到你想深入了解的影视作品"
            moreTo="/category"
          />
          <CategoryQuickEntry />
        </section>

        <section>
          <SectionHeading
            icon="ri-book-2-line"
            title="最新汉字解析"
            subtitle="从字形、笔势到文化寓意，一篇读懂影视作品里的汉字密码"
            moreTo="/special/hanzi-explanation"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {hanziArticles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <SectionHeading
              icon="ri-clapperboard-line"
              title="最新剧情介绍"
              subtitle="按章节梳理剧情脉络，支持上下滑动查看更多"
              moreTo="/special/plot-introduction"
            />
            <PlotList items={plotArticles} />
          </div>

          <div>
            <SectionHeading icon="ri-fire-line" title="热门作品榜" />
            <div className="rounded-lg bg-background-50 border border-background-200 divide-y divide-background-200">
              {[...movies].sort((a, b) => b.heat - a.heat).slice(0, 5).map((movie, index) => (
                <Link
                  key={movie.id}
                  to={`/detail/${movie.id}`}
                  className="group flex items-center gap-3 p-3 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className={`shrink-0 w-6 h-6 rounded-md font-label text-xs font-bold flex items-center justify-center ${index < 3 ? 'bg-accent-500 text-secondary-950' : 'bg-secondary-100 text-secondary-700'}`}>
                    {index + 1}
                  </span>
                  <div className="w-10 h-14 shrink-0 rounded overflow-hidden bg-background-200">
                    <img src={movie.cover} alt={`${movie.title}封面`} title={`${movie.title} - 汉字解析与剧情介绍`} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-semibold text-sm text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {movie.title}
                    </h3>
                    <p className="mt-1 text-xs font-label text-foreground-500 line-clamp-1">{movie.hanziTitle}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section>
          <SectionHeading
            icon="ri-stack-line"
            title="推荐影视资源合集"
            subtitle="围绕汉字文化主题精选的资源合集，一次看个够"
          />
          <CollectionPreview items={collections} />
        </section>

        <section className="rounded-lg bg-secondary-900 text-background-50 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="p-8 md:p-12">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-500 text-secondary-950 text-xs font-label font-semibold mb-4">
                <i className="ri-quill-pen-line w-3.5 h-3.5 flex items-center justify-center"></i>
                关于本站
              </span>
              <h2 className="font-heading font-bold text-2xl md:text-3xl leading-snug">
                在影视光影里，<br />读懂汉字的一笔一画
              </h2>
              <p className="mt-4 text-sm md:text-base font-label text-background-200/90 leading-relaxed">
                「隔壁女孩中汉字」聚焦影视作品中的汉字内容解析与剧情介绍，从字形溯源、笔势情绪到文化寓意，
                帮助影视爱好者、文化研究者与汉字爱好者，全面理解作品背后的文化内涵。
              </p>
              <div className="mt-6 flex flex-wrap gap-6">
                <div>
                  <p className="font-heading font-bold text-2xl md:text-3xl text-accent-400">{movies.length}+</p>
                  <p className="mt-1 text-xs font-label text-background-300/80">收录影视作品</p>
                </div>
                <div>
                  <p className="font-heading font-bold text-2xl md:text-3xl text-accent-400">{articles.length}+</p>
                  <p className="mt-1 text-xs font-label text-background-300/80">汉字解析 / 剧情文章</p>
                </div>
                <div>
                  <p className="font-heading font-bold text-2xl md:text-3xl text-accent-400">{collections.length}</p>
                  <p className="mt-1 text-xs font-label text-background-300/80">主题资源合集</p>
                </div>
              </div>
            </div>
            <div className="relative min-h-[220px] lg:min-h-0">
              <img
                src="https://readdy.ai/api/search-image?query=Traditional%20Chinese%20calligraphy%20and%20ink%20painting%20scene%20with%20brush%20and%20scroll%20on%20a%20dark%20wooden%20desk%2C%20warm%20amber%20light%2C%20elegant%20cultural%20atmosphere%2C%20deep%20navy%20tones&width=1200&height=900&seq=about-band-001&orientation=landscape"
                alt="汉字书法与影视文化氛围图"
                title="汉字解析与影视文化"
                className="w-full h-full object-cover object-top absolute inset-0"
              />
            </div>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}